"use client";

import { useShouldShowSaleAnnouncementNudge } from "@faire/retailer-visitor-shared/components/Header/desktop/SaleAnnouncementNudge/useShouldShowSaleAnnouncementNudge";
import { useNotificationPreview } from "@faire/retailer-visitor-shared/components/Header/HeaderNotifications/hooks/useNotificationPreview";
import { useNotificationRefetch } from "@faire/retailer-visitor-shared/components/Header/HeaderNotifications/hooks/useNotificationRefetch";
import {
  HeaderPriorityProvider,
  useHeaderPriorityStore,
} from "@faire/retailer-visitor-shared/components/HeaderPriorityRenderer/HeaderPriorityPriovider";
import { AppNudgeProvider } from "@faire/retailer-visitor-shared/features/Nudges/useAppNudgeStore";
import { usePOSSummary } from "@faire/retailer-visitor-shared/features/PointOfSale/hooks/usePOSSummary";
import { useIsLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { isMobileApp } from "@faire/retailer-visitor-shared/lib/isMobileApp";
import { useStickyNavContentPositions } from "@faire/retailer-visitor-shared/navbar/useStickyNavContentPositions";
import { useMarketStore } from "@faire/retailer-visitor-shared/stores/domain/Events/FaireMarkets/MarketStore";
import { usePromoBanners } from "@faire/retailer-visitor-shared/stores/ui/usePromoBanners";
import { useTopOfPageContent } from "@faire/retailer-visitor-shared/stores/ui/useTopOfPageContent";
import React, { useEffect } from "react";

import { ToastContainer } from "./ToastContainer";
import { useClassName } from "./useClassName";
import { useNonStickyHeader } from "./useNonStickyHeader";
import { useResizeRefCallback } from "./useResizeRefCallback";
import { useStyle } from "./useStyle";

interface WrapperProps {
  children: React.ReactNode;
  hasNoTopBanner?: boolean;
  hasToastContainer?: boolean;
}

export const TopOfPageContentWrapper: React.FC<WrapperProps> = ({
  children,
  hasNoTopBanner,
  hasToastContainer,
}) => {
  useNotificationRefetch();
  const notificationPreview = useNotificationPreview();
  return (
    <HeaderPriorityProvider
      value={{ isNotificationPreviewVisible: !!notificationPreview }}
    >
      <TopOfPageContentWrapperBase
        hasNoTopBanner={hasNoTopBanner}
        hasToastContainer={hasToastContainer}
      >
        {children}
      </TopOfPageContentWrapperBase>
    </HeaderPriorityProvider>
  );
};

const TopOfPageContentWrapperBase: React.FC<WrapperProps> = ({
  children,
  hasNoTopBanner,
  hasToastContainer,
}) => {
  const { shouldRetainOriginalPosition } = useStickyNavContentPositions();
  const { shouldShowSalesHubNudge: shouldShowSaleAnnouncementTooltip } =
    useShouldShowSaleAnnouncementNudge();

  const [{ isNotificationPreviewVisible }] = useHeaderPriorityStore([
    "isNotificationPreviewVisible",
  ]);
  const shouldHideTopNav = useIsLoggedInRetailer();
  const { setHeaderHeight } = useTopOfPageContent();
  const { bannerHeight } = usePromoBanners();
  const { isShowingCountdownBanner: isShowingMarketCountdownBanner } =
    useMarketStore(["isShowingCountdownBanner"]);
  const notificationPreview = useNotificationPreview();
  const { hasLinkedPOS } = usePOSSummary();

  const { contentCallback, contentRef } = useResizeRefCallback();
  const nonStickyHeader = useNonStickyHeader({ hasLinkedPOS });

  useEffect(() => {
    if (!shouldHideTopNav || nonStickyHeader) {
      return;
    }

    if (shouldRetainOriginalPosition) {
      setHeaderHeight(contentRef.current?.clientHeight ?? 0);
    } else {
      setHeaderHeight(0);
    }
  }, [
    shouldHideTopNav,
    shouldRetainOriginalPosition,
    nonStickyHeader,
    setHeaderHeight,
    contentRef,
  ]);

  const style = useStyle({
    bannerHeight,
    contentRef,
    hasNoTopBanner,
    isNotificationPreviewVisible,
    isShowingMarketCountdownBanner,
    nonStickyHeader,
    notificationPreview,
    shouldRetainOriginalPosition,
    shouldShowSaleAnnouncementTooltip,
  });
  const className = useClassName({
    nonStickyHeader,
    shouldRetainOriginalPosition,
  });

  if (isMobileApp()) {
    return null;
  }

  return (
    <>
      <AppNudgeProvider>
        <div
          data-test-id="header-content"
          ref={contentCallback}
          className={className}
          style={style}
        >
          {children}
        </div>
      </AppNudgeProvider>
      {hasToastContainer ? <ToastContainer style={style} /> : null}
    </>
  );
};
